/** Supported UI locales — single source of truth for language selector and locale resolution. */

export const DEFAULT_LOCALE = 'en';

export const FULLY_SUPPORTED_LOCALES = [
  { code: 'en', labelKey: 'settings.lang_en', defaultLabel: 'English', status: 'available' },
  { code: 'de', labelKey: 'settings.lang_de', defaultLabel: 'Deutsch', status: 'available' },
  // Spanish is backend-driven (no frontend bundle): served from /api/translations/es and
  // merged over the EN base. Promoted to available once the backend ES set went live.
  { code: 'es', labelKey: 'settings.lang_es', defaultLabel: 'Español', status: 'available' },
];

/** No locales are gated as coming-soon at present. */
export const COMING_SOON_LOCALES = [];

/** Header language selector — production-ready locales only. */
export const LANGUAGE_SELECTOR_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

export const SELECTABLE_LOCALE_CODES = FULLY_SUPPORTED_LOCALES.map((locale) => locale.code);

export function settingsLanguageOptions() {
  return FULLY_SUPPORTED_LOCALES;
}

export function settingsLanguageDisplayRows() {
  return [...FULLY_SUPPORTED_LOCALES, ...COMING_SOON_LOCALES];
}

export function hasSpanishLocaleFile() {
  return false;
}

export function normalizeLocaleCode(code) {
  if (!code || typeof code !== 'string') return DEFAULT_LOCALE;
  return code.split('-')[0].toLowerCase();
}

export function isSelectableLocale(code) {
  return SELECTABLE_LOCALE_CODES.includes(normalizeLocaleCode(code));
}

/** Map unsupported stored/browser/API locale values to the default production locale. */
export function resolveSupportedLocale(code) {
  const normalized = normalizeLocaleCode(code);
  return isSelectableLocale(normalized) ? normalized : DEFAULT_LOCALE;
}

/**
 * Initial locale for LanguageProvider.
 * Unsupported stored values (e.g. legacy es) fall back to en and should be persisted by caller.
 */
export function detectInitialLocale({ storedLocale, hasVisited, browserLanguage } = {}) {
  if (storedLocale) {
    return resolveSupportedLocale(storedLocale);
  }
  if (hasVisited) {
    return DEFAULT_LOCALE;
  }
  return resolveSupportedLocale(browserLanguage || DEFAULT_LOCALE);
}

/** Documented merge order used by LanguageContext (en-US bundled base + API overrides). */
export function mergeTranslations(enBase, apiTranslations) {
  return { ...enBase, ...(apiTranslations || {}) };
}

/** Same lookup order as LanguageContext.t — used in tests for missing-key behavior. */
export function translateKey(translations, key, fallback) {
  if (translations[key]) return translations[key];
  if (fallback) return fallback;
  return key;
}
